package com.siriusxm.pia.views.unifiedaggregator.replay

import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import com.siriusxm.unifiedcontent.console.aggregator.rememberCapabilities
import com.siriusxm.pia.views.unifiedaggregator.backfill.replayProbe
import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.utils.toShortDateTimeString
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.arnToConsoleUrl
import unifiedcontent.aggregator.ReplayJobStatus
import unifiedcontent.aggregator.ReplayState
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import smithy4kt.SmithyError
import kotlin.time.Duration.Companion.seconds

/**
 * Renders the detail view for a single replay job, showing its status, metadata,
 * target info, event window, and schemas. Polls for updates while the replay
 * is actively running. Provides admin-only cancel functionality.
 */
@Composable
fun ServiceViewContext.replay(
    aggregatorService: AggregatorService,
    id: String,
    replayStatus: ReplayJobStatus? = null
) {
    var status by remember { mutableStateOf(replayStatus) }
    var error by remember { mutableStateOf<SmithyError?>(null) }
    var targetName by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(id) {
        if (status == null) {
            try {
                status = aggregatorService.clientApi.getReplay(id)
                targetName = status?.targetArn?.substringAfterLast("/")
            } catch (e: SmithyError) {
                error = e
            }
        }

        while (isActive && status?.isRunning() == true) {
            status = aggregatorService.clientApi.getReplay(id)
            delay(1.seconds)
        }
    }

    suspend fun cancel() {
        try {
            aggregatorService.clientApi.cancelReplayJob(id)
        } catch (t: Throwable) {
            t.printStackTrace()
            val failure = intakeErrorMessage("cancel this replay", t)
            aggregatorService.notifications.error(failure.message, failure.description)
        }
    }

    val jobTarget = status?.targetArn
    val cancelProbe = remember(jobTarget) { jobTarget?.let { replayProbe(it) } }
    val cancelDecisions by aggregatorService.context.rememberCapabilities(listOfNotNull(cancelProbe))
    val canCancel = cancelProbe?.let { cancelDecisions?.get(it) } == true

    serviceContentView({
        title = "Replay Job"
        subTitle = targetName
        breadcrumbs {
            crumb("Aggregator", "aggregator") {
                crumb("Backfill", "backfill") {
                    crumb("Replays", "replays") {
                        crumb(id, null)
                    }
                }
            }
        }

        // Hide-only: shown when AVP allows cancelling replays against this job's target. The
        // aggregator re-checks against the stored job and answers 403/503 if that has changed.
        if (status != null && canCancel) {
            if (status?.isRunning() == true) {
                action("Cancel") {
                    cancel()
                }
            }
        }
    }) {
        if (error != null) {
            Div {
                Text("Error loading replay: ${error?.message}")
            }
        } else if (status == null) {
            spinner(size = Size.LARGE)
        } else {
            box {
                detailGrid {
                    detail("Status") {
                        replayStateIcon(status!!.state)
                        Text(" ${status!!.state.name}")
                    }
                    detail("Job ID", status?.id)
                    // Render timestamps in the globally-selected timezone (header picker).
                    detail("Started", status?.startTime?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString())
                    status?.endTime?.let {
                        detail("Completed", it.toLocalDateTimeIn(currentTimeZoneId()).toShortDateTimeString())
                    }
                    status?.stateReason?.let {
                        detail("State Reason", it)
                    }

                    detail("Event Window") {
                        val start = status?.eventStartTime?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString() ?: "N/A"
                        val end = status?.eventEndTime?.toLocalDateTimeIn(currentTimeZoneId())?.toShortDateTimeString() ?: "N/A"
                        Text("$start  to  $end")
                    }

                    detail("Target") {
                        val targetArn = status?.targetArn.orEmpty()
                        val consoleUrl = arnToConsoleUrl(targetArn)
                        if (consoleUrl != null) {
                            A(href = consoleUrl, {
                                target(ATarget.Blank)
                            }) {
                                Text(targetArn)
                            }
                        } else {
                            Text(targetArn)
                        }
                    }

                    status?.description?.let {
                        detail("Description", it)
                    }
                }
            }

            if (!status?.schemas.isNullOrEmpty()) {
                boxTable<String>("Types") {
                    items(status?.schemas)

                    column {
                        content {
                            val typeName = aggregatorService.entityType(
                                it.substringAfterLast("/").substringBefore(":")
                            )?.name ?: it
                            Text(typeName)
                        }
                    }
                }
            }
        }
    }
}
